// Read-only scaled preview of a section used as the thumbnail in library
// cards. Reuses the same React preview components the editor renders with,
// so every card shows what you'll actually get on drop — with brand merges,
// slot fills, and all the usual preview styling. No server round-trip.
//
// Sizing model (object-fit: contain style): the preview renders at its
// natural width, we measure its natural height via ResizeObserver, then
// scale uniformly so the full section fits inside `displayWidth` without
// clipping. Outer container height follows the scaled natural height so
// each card is as tall as its content needs — taller sections get taller
// cards.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PreviewNode, type PreviewNodeBaseProps } from '../EmailBuilderPreview';
import type { EmailNode } from '../types';
import type { BrandResolverContext, SlotDefault, SlotSchema } from './merge-tag-resolver';
import { resolveSectionForInsert } from './resolver';

export interface SectionCardPreviewProps {
  node: EmailNode;
  slots: Record<string, SlotSchema>;
  slotDefaults: Record<string, SlotDefault>;
  brand: BrandResolverContext | undefined;
  pickRecentMedia?: (slotName: string, used: Set<string>, offset?: number) => string | undefined;
  // Cutout-aware picker for product-cutout / transparent-product slots.
  // Returns a transparent PNG url; the resolver falls through to
  // pickRecentMedia when undefined or when it returns nothing.
  pickTransparentMedia?: (slotName: string, used: Set<string>, offset?: number) => string | undefined;
  // Resolves product slots to real variant ids so product-card sections
  // render with actual product data instead of the blank-sentinel placeholder.
  pickProduct?: (slotName: string, used: Set<string>) => string | undefined;
  // Multi-output product picker for sections that use dotted slot refs
  // (`$slot.productN.cutoutImage` / `.name` / `.price` / `.productUrl`).
  pickProductRich?: (
    slotName: string,
    used: Set<string>,
  ) => import('@nexmail/shared-util/email-builder').PickedProduct | undefined;
  // Stable rotation seed into the recent-media pool — without this every
  // card would preview url[0] in its first image slot. Caller passes a
  // hash of the section id so the same card always looks the same.
  mediaOffset?: number;
  // Inner (un-scaled) email width used by the preview — matches the node's
  // natural rendering width. Scale factor is derived to fit `displayWidth`.
  emailWidth?: number;
  // Rendered card width. The preview scales down uniformly to fit this.
  displayWidth: number;
  // Optional max height — cards that would render taller than this get
  // clipped at the bottom. Useful for enforcing a rough list cadence on
  // otherwise-very-tall sections. Omitted → no cap; card takes full height.
  maxDisplayHeight?: number;
  // Minimum height. Tiny sections (a single thin section) pad out to this
  // so cards don't collapse to a sliver. Default 60.
  minDisplayHeight?: number;
}

const NOOP_ADD = () => undefined;
const NOOP_SELECT = () => undefined;

export function SectionCardPreview({
  node,
  slots,
  slotDefaults,
  brand,
  pickRecentMedia,
  pickTransparentMedia,
  pickProduct,
  pickProductRich,
  mediaOffset,
  emailWidth = 600,
  displayWidth,
  maxDisplayHeight,
  minDisplayHeight = 60,
}: SectionCardPreviewProps) {
  // Resolve once per (node, brand) so cards don't re-pick media on every
  // render. Fresh dedupe set per card — dedupe across cards is an insert-
  // time concern, not a preview one.
  const resolvedNode = useMemo(() => {
    try {
      return resolveSectionForInsert({
        node,
        slots,
        slotDefaults,
        brand,
        pickRecentMedia,
        // Adapt the url-returning picker to the resolver's {url, productId}
        // shape. No productId paired at preview time — card previews can't
        // roundtrip to PhotoRoom; the resolver skips productId stamping when
        // it's absent. Mirrors TemplateDocumentPreview.
        pickTransparentProductMedia: pickTransparentMedia
          ? (slotName, used) => {
              const url = pickTransparentMedia(slotName, used, mediaOffset);
              return url ? { url, productId: '' } : undefined;
            }
          : undefined,
        pickProduct,
        pickProductRich,
        mediaOffset,
      });
    } catch {
      return null;
    }
  }, [node, slots, slotDefaults, brand, pickRecentMedia, pickTransparentMedia, pickProduct, pickProductRich, mediaOffset]);

  const innerRef = useRef<HTMLDivElement | null>(null);
  const [naturalHeight, setNaturalHeight] = useState<number>(0);

  // Measure the preview's NATURAL (pre-transform) height via offsetHeight —
  // getBoundingClientRect and ResizeObserver both report post-transform
  // rectangles, which would double-scale the result here. offsetHeight is
  // the layout box size and stays stable under transforms.
  useLayoutEffect(() => {
    if (!innerRef.current) return;
    setNaturalHeight(innerRef.current.offsetHeight);
  }, [resolvedNode]);

  // Re-measure on image-load reflow. Polls offsetHeight on a few rAF ticks
  // after mount so lazy-loaded images and fonts don't leave us stuck on the
  // initial 0-image-height reading. Cheap and self-terminating.
  useEffect(() => {
    if (!innerRef.current) return;
    let frame = 0;
    let last = 0;
    let ticks = 0;
    const loop = () => {
      if (!innerRef.current) return;
      const h = innerRef.current.offsetHeight;
      if (h !== last) {
        last = h;
        setNaturalHeight(h);
      }
      ticks += 1;
      // Stop after ~2s of settling — enough for typical image loads.
      if (ticks < 120) frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [resolvedNode]);

  if (!resolvedNode) {
    return (
      <Box
        sx={{
          width: displayWidth,
          height: minDisplayHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Preview unavailable
        </Typography>
      </Box>
    );
  }

  const scale = displayWidth / emailWidth;
  const scaledHeight = naturalHeight * scale;
  const outerHeight = Math.max(
    minDisplayHeight,
    maxDisplayHeight ? Math.min(scaledHeight, maxDisplayHeight) : scaledHeight,
  );

  const previewProps: PreviewNodeBaseProps = {
    selectedNodeId: null,
    onSelectNode: NOOP_SELECT,
    onRequestAdd: NOOP_ADD,
    brandPrimaryColor: brand?.colorByUsage?.primary,
    brandLogoUrl: brand?.logoUrl,
    brandName: brand?.name,
  };

  return (
    <Box
      sx={{
        width: displayWidth,
        height: outerHeight,
        overflow: 'hidden',
        bgcolor: '#FFFFFF',
        position: 'relative',
        pointerEvents: 'none',
        transition: 'height 0.15s ease-out',
      }}
    >
      <Box
        ref={innerRef}
        sx={{
          width: emailWidth,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <PreviewNode node={resolvedNode} {...previewProps} />
      </Box>
    </Box>
  );
}
